import { buildShipperBreakdown } from 'src/components/dispatch/detailPanel/shipperBreakdown';
import { UnassignedItem } from 'src/entities/dispatch';
import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import { buildDisplayInfoFields } from './displayInfoFields';
import { LaneGroup } from './laneGroups';

/**
 * 座標編集 (logposeTech/log_web_issues#315) の対象 legRun。レーンは同じ区間の集約なので
 * 配下を全件まとめて更新する（ユーザー決定 2026-09-10）。
 *
 * **先頭は必ず代表 (head) の legRun**。詳細パネルが出す座標は代表から採っている
 * （buildLaneDetailItem の「地点そのものの情報は代表から」）ので、引き直し
 * (detailSelectionSync の resolveUnassignedGeoFromLatest) が別の伝票を見ると、
 * 表示中の座標と引き直し元が別案件になる。代表から引けないときは全体を空にして
 * 編集不可へ倒す — 配下の id だけ並べるより、直せないほうが安全。
 *
 * `allLegRunIds` はフィルタ前の全件から集めたもの（laneGroups の buildLaneLegRunIdsByKey）。
 * group.items は filter → search →「選択したものだけ表示」を通った後の集合なので、
 * これを渡さないと隠れた配下が更新から落ちる。
 */
function laneLegRunIds(group: LaneGroup, allLegRunIds?: readonly number[]): number[] {
  const [head] = group.items;
  const headIds = head?.legRunIds ?? [];
  if (headIds.length === 0) return [];

  const rest = allLegRunIds ?? group.items.flatMap((item) => item.legRunIds ?? []);
  return [...new Set([...headIds, ...rest])];
}

/**
 * レーンの詳細パネルに渡す 1 件。配下の伝票をまとめた合成 item。
 *
 * 代表 1 件をそのまま渡すと、他の伝票の数量と荷主が詳細から消える。
 * 地点そのものの情報（名前・住所・座標）は代表から採る — レーンは同じ区間の集約なので
 * どれも同じ値になる。
 */
export function buildLaneDetailItem(group: LaneGroup, allLegRunIds?: readonly number[]): UnassignedItem {
  const [head] = group.items;
  const legRuns = group.items
    .map((item) => item.source)
    .filter((source): source is ShipmentOrderLegRunForPlanEntity => source != null);

  const orderIds = new Set(
    legRuns.map((lr) => lr.shipment_order_id).filter((id): id is number => typeof id === 'number'),
  );
  const shippers = [...new Set(legRuns.map((lr) => lr.shipper_name).filter(Boolean))];

  return {
    ...head,
    // 詳細パネルは selection の id で開閉を判定する。レーンごとに変わる値にする
    id: group.key,
    // 編集は案件でなくレーンを開く（配下の伝票が複数あり案件が 1 つに決まらない）
    isLaneGroup: true,
    legRunIds: laneLegRunIds(group, allLegRunIds),
    cases: group.totalCases,
    weight: group.totalWeight,
    volume: group.totalVolume,
    invoiceCount: orderIds.size,
    shippers,
    shipperBreakdown: buildShipperBreakdown(legRuns),
    // 代表 1 件ではなくレーン配下の全伝票から組み立てる（head の値だけだと他の伝票が消える）
    ...buildDisplayInfoFields(legRuns),
  };
}
